import { z } from 'zod';
import { CuisineSchema, DietaryOptionSchema, ServiceTypeSchema } from './supplier-schema.js';
/**
 * Anfrage / Ausschreibung — das Kernobjekt. Siehe `docs/02_DOMAIN_MODEL.md` §2.2.
 *
 * Zwei Regeln, die dieses Modul trägt:
 *
 * 1. **`bidsCloseAt` ist die Wahrheit, nicht `status`.** Der abgeleitete Zustand wird beim Lesen
 *    berechnet ({@link effectiveRfqStatus}); der Cron macht nur Seiteneffekte (D3).
 * 2. **Die exakte Adresse ist ein Geheimnis.** Vor dem Zuschlag sehen Lieferanten nur PLZ und
 *    Stadt. Das ist der wirksamste technische Umgehungsschutz im System — deshalb sind die
 *    Sichten eigene Schemas, keine Laufzeit-Teilmengen.
 */
export const RfqStatusSchema = z.enum([
    /** Entsteht im Chat, noch nicht abgesendet. Kein Geschäftsvorfall. */
    'draft',
    /** Kunde hat abgesendet, wartet auf Freigabe/Prüfung. */
    'submitted',
    /** An Lieferanten verteilt, Angebotsfrist läuft. */
    'broadcast',
    /** Frist abgelaufen, Angebote liegen vor, Kunde wählt. */
    'closed',
    /** Zuschlag erteilt. */
    'awarded',
    /** Leistung erbracht — Provision wird fällig. */
    'completed',
    /** Vom Kunden zurückgezogen (jederzeit vor Zuschlag). */
    'cancelled',
    /** Abgelehnt bei der Prüfung (Spam, unbedienbar). */
    'rejected',
    /** Ohne Auswahl verfallen oder ohne ein einziges Angebot geschlossen. */
    'expired',
]);
/** Zustände, in denen ein Angebot abgegeben werden darf. */
export const RFQ_STATUS_ACCEPTING_OFFERS = ['broadcast'];
/**
 * Der abgeleitete Zustand — **die einzige Stelle, an der aus Frist und Status ein Ergebnis wird**.
 *
 * Jede Leseoperation geht hier durch. Fällt der Cron aus, zeigt die UI trotzdem das Richtige;
 * nur die Mails kommen verspätet. Genau diese Fehlertoleranz ist der Grund für D3.
 *
 * @param now Vergleichszeitpunkt — injizierbar, damit Tests keine Systemzeit brauchen.
 */
export function effectiveRfqStatus(stored, now = new Date()) {
    if (stored.status !== 'broadcast' || stored.bidsCloseAt === null)
        return stored.status;
    return now.getTime() >= Date.parse(stored.bidsCloseAt) ? 'closed' : 'broadcast';
}
/**
 * Wie Preise dem Kunden gezeigt werden. `net` für Firmen und Organisationen, `gross` für
 * Privatpersonen (D38) — die Preisangabenverordnung verlangt gegenüber Verbrauchern den
 * Gesamtpreis einschließlich Umsatzsteuer. Abgeleitet aus {@link RequesterTypeSchema} über
 * {@link priceDisplayFor}, nie getrennt davon gesetzt.
 */
export const PriceDisplaySchema = z.enum(['net', 'gross']);
/**
 * Wer anfragt: eine Firma, ein Verein, eine Behörde (`business`) oder eine Privatperson
 * (`private`). Bis D38 gab es nur `business` (D15).
 */
export const RequesterTypeSchema = z.enum(['business', 'private']);
/** Die eine Regel, welche Preisdarstellung zu welchem Kunden gehört. */
export function priceDisplayFor(requesterType) {
    return requesterType === 'private' ? 'gross' : 'net';
}
/* --------------------------------------------------------------------------------------------
 * Felder ohne Vorgabewerte — die gemeinsame Basis
 *
 * ⚠️ **Warum die Vorgabewerte getrennt stehen.** Zod wendet ein `.default()` auch dann an, wenn
 * das Feld über `.partial()` optional gemacht wurde: `z.object({ a: z.array(X).default([]) })
 * .partial().parse({})` liefert `{ a: [] }`, nicht `{}`.
 *
 * Für den Gesprächsentwurf ist das kein Schönheitsfehler, sondern ein Datenverlust. Das
 * Modell sendet bei einer Korrektur nur das geänderte Feld (`{ guestCount: 60 }`); nach dem
 * Parsen enthielte diese Teilangabe zusätzlich `cuisines: []`, `dietaryOptions: []` und
 * `notes: ''`. Und weil {@link mergeRequirementsDraft} Listen bewusst **ersetzt** statt sie zu
 * verschmelzen, hätte jede Korrektur der Gästezahl die Ernährungswünsche gelöscht — still,
 * und erst in der Ausschreibung sichtbar.
 *
 * Deshalb: Die Felder werden einmal **ohne** Vorgabewerte definiert. Das gespeicherte Objekt
 * ({@link RfqRequirementsSchema}) legt sie darauf, der Entwurf
 * ({@link RfqRequirementsDraftSchema}) nicht.
 * ----------------------------------------------------------------------------------------- */
const eventLocationFields = {
    /** Nur für Eigentümer, Admin und den Gewinner sichtbar. */
    street: z.string().min(1).max(200).nullable(),
    postalCode: z.string().regex(/^\d{5}$/, 'Deutsche PLZ mit fünf Ziffern'),
    city: z.string().min(1).max(120),
    /** Freitext: "3. OG, Konferenzraum Nord", "Anlieferung über Hof". Wie die Straße geschützt. */
    accessNotes: z.string().max(1000).nullable(),
};
/** Veranstaltungsort. Die Straße ist getrennt gehalten, weil sie eigenen Schutz braucht. */
export const EventLocationSchema = z.object({
    ...eventLocationFields,
    street: eventLocationFields.street.default(null),
    accessNotes: eventLocationFields.accessNotes.default(null),
});
/**
 * Das Ergebnis der KI-Bedarfsaufnahme — was das Modell über `save_requirements` /
 * `submit_request` füllt (D5). Alles optional zu halten wäre bequem, macht aber den
 * Hard-Filter im Matching unmöglich; deshalb ist der Pflichtkern klein, aber verbindlich.
 */
const requirementFields = {
    eventDate: z.iso.date(),
    /** Lokale Uhrzeit `HH:MM`. Ohne Zeitzone — der Ort steht daneben. */
    eventStartTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    eventDurationMinutes: z
        .int()
        .min(15)
        .max(24 * 60)
        .nullable(),
    guestCount: z.int().min(1).max(10_000),
    location: EventLocationSchema,
    serviceTypes: z.array(ServiceTypeSchema).min(1),
    cuisines: z.array(CuisineSchema),
    dietaryOptions: z.array(DietaryOptionSchema),
    /** Anzahl Gäste je Ernährungsform, soweit bekannt. Schlüssel ist eine `DietaryOption`. */
    dietaryCounts: z.partialRecord(DietaryOptionSchema, z.int().min(0)),
    /** Budgetrahmen netto in Cent, falls der Kunde einen genannt hat. */
    budgetNetCentsMin: z.int().min(0).nullable(),
    budgetNetCentsMax: z.int().min(0).nullable(),
    /** Braucht es Personal, Geschirr, Aufbau? Bestimmt später auch den USt-Satz (7 % vs. 19 %). */
    needsStaff: z.boolean(),
    needsTableware: z.boolean(),
    needsDelivery: z.boolean(),
    /** Freitext des Kunden, unverändert. Wird nie ins Matching gegeben, nur angezeigt. */
    notes: z.string().max(4000),
};
export const RfqRequirementsSchema = z.object({
    ...requirementFields,
    eventDurationMinutes: requirementFields.eventDurationMinutes.default(null),
    cuisines: requirementFields.cuisines.default([]),
    dietaryOptions: requirementFields.dietaryOptions.default([]),
    dietaryCounts: requirementFields.dietaryCounts.default({}),
    budgetNetCentsMin: requirementFields.budgetNetCentsMin.default(null),
    budgetNetCentsMax: requirementFields.budgetNetCentsMax.default(null),
    needsStaff: requirementFields.needsStaff.default(false),
    needsTableware: requirementFields.needsTableware.default(false),
    needsDelivery: requirementFields.needsDelivery.default(true),
    notes: requirementFields.notes.default(''),
});
/* --------------------------------------------------------------------------------------------
 * Der Entwurf während des Gesprächs (Phase 4)
 * ----------------------------------------------------------------------------------------- */
/**
 * Was während der Bedarfsaufnahme entsteht — jedes Feld optional, bis es gesetzt ist.
 *
 * Anders als ein bloßes `RfqRequirementsSchema.partial()` ist auch der **Ort** teilbefüllbar.
 * Ein Kunde nennt fast immer zuerst die Stadt und die Postleitzahl erst auf Nachfrage. Wäre
 * `location` nur als Ganzes speicherbar, müsste das Modell die Stadt so lange zurückhalten —
 * und in der Praxis vergisst es sie dann.
 */
export const RfqRequirementsDraftSchema = z
    .object({
    ...requirementFields,
    // Auch der Ort ohne Vorgabewerte — sonst schriebe jede Teilangabe `street: null` mit.
    location: z.object(eventLocationFields).partial(),
})
    .partial();
/** Ein fehlendes Pflichtfeld: Feldname für das Modell, Beschriftung für den Menschen. */
export const RequirementGapSchema = z.object({
    field: z.string(),
    label: z.string(),
});
/**
 * Deutsche Beschriftungen der Anforderungsfelder — an **einer** Stelle (D14).
 *
 * Sie erscheinen im Chat-Fortschritt („was noch fehlt"), in der Statusseite des Kunden und in
 * der Lieferantenansicht. Drei Kopien wären drei Schreibweisen derselben Sache.
 */
export const REQUIREMENT_FIELD_LABELS = {
    eventDate: 'Datum der Veranstaltung',
    eventStartTime: 'Uhrzeit',
    eventDurationMinutes: 'Dauer',
    guestCount: 'Anzahl der Gäste',
    'location.postalCode': 'Postleitzahl des Veranstaltungsorts',
    'location.city': 'Ort',
    'location.street': 'Straße und Hausnummer',
    'location.accessNotes': 'Hinweise zur Anlieferung',
    serviceTypes: 'Art der Bewirtung',
    cuisines: 'Küchenrichtung',
    dietaryOptions: 'Ernährungsformen',
    dietaryCounts: 'Anzahl je Ernährungsform',
    budgetNetCentsMin: 'Budgetrahmen (von)',
    budgetNetCentsMax: 'Budgetrahmen (bis)',
    needsStaff: 'Servicepersonal',
    needsTableware: 'Geschirr und Besteck',
    needsDelivery: 'Anlieferung',
    notes: 'Weitere Hinweise',
};
/**
 * Die **eine** Vollständigkeitsregel der Bedarfsaufnahme.
 *
 * Sie steuert drei Dinge, und genau deshalb steht sie hier und nicht im Handler:
 *
 * 1. Den Rückgabewert von `save_requirements` — das Modell sieht, was noch fehlt, und fragt
 *    danach. Das Gesprächsziel steht dadurch im Code, nicht in der Prompt-Prosa.
 * 2. Die serverseitige Sperre vor `submit_request`. **Das Modell ist hier nicht die letzte
 *    Instanz** (`03_AI_CHAT.md` §A.2).
 * 3. Die Fortschrittsanzeige der Chat-Island.
 *
 * Der Pflichtkern ist bewusst klein: Alles, was der Hard-Filter des Matchings braucht (D8) —
 * wann, wo, wie viele, welche Art von Bewirtung. Alles andere ist Kür und darf fehlen.
 *
 * @param now Vergleichszeitpunkt für die Datumsprüfung. Injizierbar für Tests.
 */
export function missingRequirementFields(draft, now = new Date()) {
    const gaps = [];
    const push = (field, label) => gaps.push({ field, label: label ?? REQUIREMENT_FIELD_LABELS[field] ?? field });
    if (!draft.eventDate) {
        push('eventDate');
    }
    else {
        /**
         * Ein Datum in der Vergangenheit ist kein fehlendes, sondern ein falsches Feld — es fällt
         * hier trotzdem an, weil es dieselbe Konsequenz hat: Die Anfrage darf so nicht raus. Ohne
         * diese Zeile erzeugt ein Vertipper („2025" statt „2026") eine Ausschreibung, die kein
         * Lieferant bedienen kann und die trotzdem Einladungen verschickt.
         */
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);
        if (Date.parse(`${draft.eventDate}T23:59:59`) < startOfToday.getTime()) {
            push('eventDate', 'Datum der Veranstaltung (muss in der Zukunft liegen)');
        }
    }
    if (!draft.eventStartTime)
        push('eventStartTime');
    if (typeof draft.guestCount !== 'number' || draft.guestCount < 1)
        push('guestCount');
    if (!draft.location?.postalCode)
        push('location.postalCode');
    if (!draft.location?.city)
        push('location.city');
    if (!draft.serviceTypes || draft.serviceTypes.length === 0)
        push('serviceTypes');
    return gaps;
}
/**
 * Fügt eine Teilangabe in den bestehenden Entwurf ein.
 *
 * **Objekte werden verschmolzen, Listen ersetzt.** Das ist keine Willkür, sondern folgt dem,
 * was ein Mensch meint: „Ach, die PLZ ist 41460" ergänzt den Ort, während „doch nur
 * vegetarisch" die bisherige Liste **ersetzt** und nicht erweitert. Ein Merge von Listen
 * machte jede Korrektur unmöglich — der Kunde könnte nie etwas zurücknehmen.
 */
export function mergeRequirementsDraft(current, patch) {
    const merged = { ...current, ...patch };
    if (patch.location) {
        merged.location = { ...current.location, ...patch.location };
    }
    if (patch.dietaryCounts) {
        merged.dietaryCounts = { ...current.dietaryCounts, ...patch.dietaryCounts };
    }
    return merged;
}
/* --------------------------------------------------------------------------------------------
 * Der Ausschreibungstext (Phase 5)
 * ----------------------------------------------------------------------------------------- */
/**
 * Was aus dem Bedarf für die Lieferanten wird (`03_AI_CHAT.md` §B).
 *
 * **Das Modell schreibt Prosa, das System schreibt Fakten.** Gästezahl, Datum, Uhrzeit und
 * Diätangaben werden aus {@link RfqRequirementsSchema} gerendert und stehen **nicht** in
 * diesen Feldern — sonst wandert eine halluzinierte Zahl in die Ausschreibung und keiner
 * merkt es, bis die Angebote für 45 statt 145 Personen kalkuliert sind.
 *
 * `source` ist kein Zierrat: Wenn der Bedrock-Aufruf scheitert, rendert der Server ein reines
 * Template (`'template'`). Eine Anfrage darf nie hängenbleiben, weil ein LLM nicht antwortet
 * — aber man muss hinterher sehen können, welcher Text woher kam.
 */
export const RfqBriefSchema = z.object({
    /** Eine Zeile, wie sie in der Betreffzeile der Einladungsmail steht. */
    title: z.string().min(1).max(160),
    /** Drei bis vier Sätze sachliches Deutsch. */
    summary: z.string().min(1).max(2000),
    /** Normalisierte Stichpunkte für die Lieferantenansicht. */
    requirementList: z.array(z.string().max(300)).max(20),
    /** Was der Lieferant klären sollte — „Anlieferzeitfenster?", „Stromanschluss vorhanden?" */
    openQuestions: z.array(z.string().max(300)).max(10),
    source: z.enum(['ai', 'template']),
    generatedAt: z.iso.datetime(),
});
/** Vollständiger RFQ-Datensatz, wie er in DynamoDB liegt. */
export const RfqRecordSchema = z.object({
    rfqId: z.string().min(1),
    status: RfqStatusSchema,
    requirements: RfqRequirementsSchema,
    // Identität des Kunden (D6). Kein Signup im MVP — die E-Mail ist die Identität.
    /** Verweis auf das `REQ#`-Item. Trägt die Adresse, die Anfrage trägt die Kopie. */
    requesterId: z.string().min(1),
    requesterEmail: z.email(),
    requesterEmailVerifiedAt: z.iso.datetime().nullable().default(null),
    requesterName: z.string().max(200).nullable().default(null),
    requesterCompany: z.string().max(200).nullable().default(null),
    /**
     * Nur für uns und — nach dem Zuschlag — für den Gewinner (§5). Steht auf der Anfrage und
     * nicht nur am Kunden-Item, weil eine Durchwahl je Veranstaltung wechseln kann.
     */
    requesterPhone: z.string().max(40).nullable().default(null),
    /** Ab Tag 1 im Schema, im MVP immer `null` — damit spätere Konten ohne Migration passen. */
    requesterUserId: z.string().nullable().default(null),
    /** Firma/Organisation oder Privatperson (D38). Anfragen vor D38 tragen das Feld nicht: `business`. */
    requesterType: RequesterTypeSchema.default('business'),
    /** Folgt aus `requesterType` ({@link priceDisplayFor}): `gross` für Privatpersonen (D38). */
    priceDisplay: PriceDisplaySchema.default('net'),
    /** Beim Broadcast eingefroren (D16). Vor dem Broadcast `null`. */
    commissionRateBp: z.int().min(0).max(10000).nullable().default(null),
    /**
     * Die Frist. Ab `broadcast` gesetzt, danach unveränderlich außer durch explizite
     * Verlängerung (dokumentiert in der Activity). 6 h ist Default, kein Gesetz (D12).
     */
    bidsCloseAt: z.iso.datetime().nullable().default(null),
    /** Die beim Broadcast gewählte Fensterlänge in Geschäftsstunden — fürs Protokoll. */
    bidWindowHours: z
        .int()
        .min(1)
        .max(24 * 30)
        .nullable()
        .default(null),
    broadcastAt: z.iso.datetime().nullable().default(null),
    closedAt: z.iso.datetime().nullable().default(null),
    /**
     * Gesetzt, wenn **der Kunde** die Angebotsphase vor Fristablauf beendet hat (D23) — sonst
     * `null`, auch bei einer regulär abgelaufenen Frist.
     *
     * Es ist bewusst ein eigener Zeitstempel und kein Flag: Der Abstand zu `bidsCloseAt` ist die
     * Zahl, die zählt. Schließen Kunden regelmäßig Stunden zu früh, ist das Standardfenster aus
     * D12 zu lang — und diese Auswertung braucht beide Zeitpunkte, nicht ein `true`.
     */
    closedEarlyAt: z.iso.datetime().nullable().default(null),
    /**
     * Wann der Kunde über das **erste** eingegangene Angebot benachrichtigt wurde (D23).
     *
     * Trägt dieselbe Rolle wie `remindedAt` weiter unten: Er ist die Bedingung, an der genau
     * ein Aufruf die Mail verschicken darf. Ohne ihn bekäme der Kunde bei acht Einladungen acht
     * Mails — und die achte liest niemand mehr.
     */
    firstOfferNotifiedAt: z.iso.datetime().nullable().default(null),
    /**
     * Wann die T-2-h-Erinnerung an die Lieferanten ohne Angebot rausging.
     *
     * Steht auf der Anfrage und nicht an jeder Einladung: Der Sweeper läuft alle fünf Minuten
     * und muss in **einem** bedingten Schreibvorgang entscheiden können, ob dieser Lauf die
     * Erinnerung verschickt. Ein Feld je Einladung wäre ein Dutzend Bedingungen statt einer.
     */
    remindedAt: z.iso.datetime().nullable().default(null),
    /** Wie viele Lieferanten eingeladen wurden. Der Kunde darf das sehen, der Lieferant nicht. */
    inviteCount: z.int().min(0).default(0),
    /** Der generierte Ausschreibungstext (§B). Vor dem Broadcast `null`. */
    brief: RfqBriefSchema.nullable().default(null),
    awardedSupplierId: z.string().nullable().default(null),
    awardedAt: z.iso.datetime().nullable().default(null),
    /**
     * Wann der Betreiber die **Übergabe** freigegeben hat — der Moment, in dem die Kontaktdaten
     * in beide Richtungen fließen (D24).
     *
     * ### Warum der Zuschlag und die Übergabe zwei Ereignisse sind
     *
     * Bis Phase 6 war beides eins: Der Kunde klickte „verbindlich beauftragen", und in derselben
     * Sekunde bekam der Betrieb Name, Telefonnummer und Hausnummer. Damit war die Provision
     * verdient und das einzige Druckmittel dahin — caterinx hat keinen Zahlungsdienstleister
     * (D19), fakturiert nach Leistung und kann eine bereits übergebene Adresse nicht
     * zurückholen. Ein Betrieb, der nicht zahlen will, hatte den Auftrag trotzdem.
     *
     * Der Zwischenschritt dreht die Reihenfolge um: Zuschlag → Provisionsrechnung →
     * Zahlungseingang → Freigabe → Kontaktdaten. Der Kunde erfährt sofort, dass sein Auftrag
     * angekommen ist; der Betrieb erfährt sofort, dass er gewonnen hat. Was wartet, sind
     * ausschließlich die Kontaktdaten.
     *
     * ### Warum ein Zeitstempel und kein Status
     *
     * Aus demselben Grund wie bei `bidsCloseAt` (Goldene Regel 4): Der Zustand `awarded` bleibt
     * `awarded`. Ein eigener Status hätte jede Statusabfrage, jede Beschriftungstabelle und
     * jede Zeile der Autorisierungsmatrix um einen Fall erweitert — für eine Information, die
     * genau zwei Stellen brauchen: die Kontaktfreigabe und die Admin-Ansicht.
     *
     * `null` heißt: erteilt, aber noch nicht übergeben.
     */
    handoverReleasedAt: z.iso.datetime().nullable().default(null),
    /** Wer freigegeben hat (Cognito-`sub` des Admins). Nur fürs Protokoll. */
    handoverReleasedBy: z.string().nullable().default(null),
    /**
     * Warum abgelehnt oder zurückgezogen. Wird dem Kunden auf der Statusseite gezeigt — eine
     * Ablehnung ohne Begründung ist im Kundenkontakt nicht zustellbar.
     */
    statusReason: z.string().max(1000).nullable().default(null),
    /** Herkunfts-Chat, für die Nachvollziehbarkeit. */
    chatSessionId: z.string().nullable().default(null),
    /** Generierter Ausschreibungstext ab einer gewissen Größe in S3 (siehe §4.3). */
    briefS3Key: z.string().nullable().default(null),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * Der Stand einer Einladung. Steht **vor** den Sichten, weil beide ihn brauchen — der
 * Datensatz weiter unten und die Lieferantenansicht direkt darunter.
 */
export const RfqInviteStatusSchema = z.enum(['invited', 'viewed', 'declined', 'offered']);
/** Deutsche Beschriftungen der Einladungsstände — an einer Stelle (D14). */
export const RFQ_INVITE_STATUS_LABELS = {
    invited: 'Eingeladen',
    viewed: 'Angesehen',
    declined: 'Abgesagt',
    offered: 'Angebot abgegeben',
};
/**
 * Was ein eingeladener Lieferant sieht.
 *
 * **Ohne exakte Adresse, ohne Kundenkontakt, ohne jede Information über Mitbewerber** — auch
 * nicht deren Anzahl (§5, Punkt 1). PLZ und Stadt reichen für die Kalkulation der Anfahrt.
 */
export const RfqSupplierViewSchema = z.object({
    rfqId: z.string().min(1),
    status: RfqStatusSchema,
    bidsCloseAt: z.iso.datetime().nullable(),
    eventDate: z.iso.date(),
    eventStartTime: z.string(),
    eventDurationMinutes: z.int().nullable(),
    guestCount: z.int(),
    postalCode: z.string(),
    city: z.string(),
    serviceTypes: z.array(ServiceTypeSchema),
    cuisines: z.array(CuisineSchema),
    dietaryOptions: z.array(DietaryOptionSchema),
    dietaryCounts: z.partialRecord(DietaryOptionSchema, z.int().min(0)),
    needsStaff: z.boolean(),
    needsTableware: z.boolean(),
    needsDelivery: z.boolean(),
    notes: z.string(),
    /** Der generierte Ausschreibungstext. `null`, solange keiner erzeugt wurde. */
    brief: RfqBriefSchema.nullable(),
    /** Eingefrorener Satz — der Lieferant muss vor der Abgabe wissen, was ihn erwartet (D10). */
    commissionRateBp: z.int().min(0).max(10000),
    /**
     * Ob eine Privatperson anfragt (D38). Kein Kontaktdatum, aber für die Kalkulation wichtig:
     * Der Kunde sieht dann den Bruttopreis, und die Rechnung geht an einen Verbraucher.
     */
    requesterType: RequesterTypeSchema.default('business'),
    /** Der eigene Einladungsstand. Nie der von anderen. */
    inviteStatus: RfqInviteStatusSchema,
    /**
     * Ob die Übergabe freigegeben ist (D24). Für den Gewinner die Antwort auf „wo sind die
     * Kontaktdaten?"; für alle anderen ohne Bedeutung und deshalb `false`.
     */
    handoverReleased: z.boolean().default(false),
    /**
     * Der eigene Anfahrtszuschlag für dieses PLZ-Gebiet, in Cent. Eigene Stammdaten — kein
     * Rückschluss auf Mitbewerber, aber eine echte Hilfe bei der Kalkulation.
     */
    travelSurchargeCents: z.int().min(0),
});
/**
 * Eine Zeile in der Ausschreibungsliste des Lieferanten.
 *
 * Bewusst schmal und **ohne jede Zahl über Mitbewerber** (§5, Punkt 1). Was hier fehlt, ist
 * so wichtig wie das, was drinsteht.
 */
export const RfqSupplierListItemSchema = z.object({
    rfqId: z.string().min(1),
    status: RfqStatusSchema,
    bidsCloseAt: z.iso.datetime().nullable(),
    title: z.string(),
    eventDate: z.iso.date(),
    eventStartTime: z.string(),
    guestCount: z.int(),
    postalCode: z.string(),
    city: z.string(),
    serviceTypes: z.array(ServiceTypeSchema),
    inviteStatus: RfqInviteStatusSchema,
    /** Ob dieser Betrieb bereits ein gültiges Angebot abgegeben hat. */
    hasOffer: z.boolean(),
    invitedAt: z.iso.datetime(),
});
/** Was der Kunde auf seiner Statusseite sieht (per Magic-Link, ohne Login). */
export const RfqRequesterViewSchema = z.object({
    rfqId: z.string().min(1),
    status: RfqStatusSchema,
    bidsCloseAt: z.iso.datetime().nullable(),
    requirements: RfqRequirementsSchema,
    priceDisplay: PriceDisplaySchema,
    /** Der Kunde darf die Zahl der eingegangenen Angebote sehen — der Lieferant nicht. */
    offerCount: z.int().min(0),
    /** Wie viele Betriebe angeschrieben wurden. Ebenfalls nur für den Kunden. */
    inviteCount: z.int().min(0),
    /** Wie viele davon abgesagt haben — ehrlich, statt den Kunden warten zu lassen. */
    declinedCount: z.int().min(0),
    /**
     * Von wie vielen Betrieben **noch** eine Antwort aussteht: eingeladen, aber weder Angebot
     * noch Absage.
     *
     * **Die Zahl, die das vorzeitige Schließen trägt** (D23). Der Kunde darf jederzeit
     * beenden — damit das eine informierte Entscheidung ist und keine ungeduldige, muss er
     * sehen, worauf er verzichtet. „Noch 4 erwartet" hält jemanden zurück, „3 Angebote" allein
     * nicht.
     *
     * Serverseitig aus den Einladungen gezählt, nicht als `inviteCount - offerCount -
     * declinedCount` gerechnet: Ein zurückgezogenes Angebot senkt `offerCount`, ohne dass der
     * Betrieb wieder zu einem wird, auf den man wartet.
     */
    pendingCount: z.int().min(0),
    /** Ob der Kunde die Angebotsphase selbst vorzeitig beendet hat (D23). */
    closedEarly: z.boolean(),
    brief: RfqBriefSchema.nullable(),
    broadcastAt: z.iso.datetime().nullable(),
    awardedSupplierId: z.string().nullable(),
    /**
     * Ob die Übergabe an den Betrieb freigegeben ist (D24).
     *
     * Zwischen Zuschlag und Freigabe steht der Vorgang bei uns: Der Kunde hat entschieden, der
     * Betrieb weiß Bescheid, aber die Kontaktdaten sind noch nicht ausgetauscht. Ohne dieses
     * Feld sähe die Statusseite in dieser Zeit aus wie ein Fehler — „Auftrag vergeben" und
     * darunter keine Kontaktdaten.
     */
    handoverReleased: z.boolean().default(false),
    /** Bei `rejected` und `cancelled`: warum. Sonst `null`. */
    statusReason: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/** Einladung eines Lieferanten zu einer Ausschreibung (§2.3). */
export const RfqInviteSchema = z.object({
    rfqId: z.string().min(1),
    supplierId: z.string().min(1),
    status: RfqInviteStatusSchema,
    /** SHA-256 des Magic-Link-Tokens. Das Token selbst wird nie gespeichert (D7). */
    magicTokenHash: z.string().length(64),
    invitedAt: z.iso.datetime(),
    deliveredAt: z.iso.datetime().nullable().default(null),
    viewedAt: z.iso.datetime().nullable().default(null),
    respondedAt: z.iso.datetime().nullable().default(null),
    /** Warum abgesagt wurde. Auch Absagen sind wertvoll — sie bewerten das Matching. */
    declineReason: z.string().max(500).nullable().default(null),
    /**
     * Der für **diesen** Lieferanten geltende Provisionssatz, eingefroren beim Broadcast.
     *
     * D16 verlangt zwei Dinge, die sich zu widersprechen scheinen: Der Satz ist pro Lieferant
     * überschreibbar (Einführungsrabatt für die ersten Partner) **und** pro Vorgang
     * eingefroren. Beides gilt, wenn der Plattform-Default auf der Anfrage einfriert und der
     * effektive Satz auf der Einladung. Ein später geänderter Lieferantensatz fasst laufende
     * Ausschreibungen damit nicht an — und zwei Betriebe können in derselben Ausschreibung
     * unterschiedliche Konditionen haben, ohne dass einer vom anderen etwas erfährt.
     */
    commissionRateBp: z.int().min(0).max(10000),
    /** Anfahrtszuschlag dieses Betriebs für das PLZ-Gebiet, kopiert beim Broadcast. */
    travelSurchargeCents: z.int().min(0).default(0),
});
/* --------------------------------------------------------------------------------------------
 * Einladung per E-Mail an einen noch nicht registrierten Betrieb (D32)
 * ----------------------------------------------------------------------------------------- */
/**
 * Der Stand einer E-Mail-Einladung.
 *
 * **Nur zwei Stände, weil es nur einen Übergang gibt.** Was danach passiert — angesehen,
 * angeboten, abgesagt —, steht an der gewöhnlichen {@link RfqInviteSchema}, die beim Annehmen
 * entsteht. Eine zweite Zustandsmaschine daneben liefe irgendwann auseinander.
 */
export const RfqExternalInviteStatusSchema = z.enum(['sent', 'claimed']);
export const RFQ_EXTERNAL_INVITE_STATUS_LABELS = {
    sent: 'Verschickt',
    claimed: 'Angenommen',
};
/**
 * Was der Betreiber nach einem Anruf einträgt.
 *
 * Die Adresse wird serverseitig kleingeschrieben — sie ist der Schlüssel der Einladung, und
 * `Info@` und `info@` sind derselbe Betrieb. Betriebsname und Nachricht sind optional und
 * leer statt `null`, weil sie aus Formularfeldern kommen.
 */
export const RfqExternalInviteInputSchema = z.object({
    email: z.email().max(254),
    /** Wie der Betrieb am Telefon hieß. Nur für Anrede und Admin-Liste — keine Stammdaten. */
    companyName: z.string().trim().max(200).default(''),
    /** Persönliche Zeile über den Eckdaten, etwa „wie eben besprochen". */
    note: z.string().trim().max(1000).default(''),
});
/**
 * Was der eingeladene Betrieb über den Link sieht — **vor** jeder Anmeldung.
 *
 * Eine Einschlussliste aus {@link RfqSupplierViewSchema}, also dieselbe Grenze wie in der
 * Einladungsmail: PLZ und Stadt, keine Straße, kein Kundenname, keine Zahl über Mitbewerber
 * (§5, D17). Was fehlt, sind die Felder, die eine Einladung voraussetzen — Einladungsstand,
 * Übergabe, Anfahrtszuschlag; die gibt es erst nach dem Annehmen.
 */
export const RfqExternalInvitePreviewSchema = RfqSupplierViewSchema.pick({
    rfqId: true,
    status: true,
    bidsCloseAt: true,
    eventDate: true,
    eventStartTime: true,
    eventDurationMinutes: true,
    guestCount: true,
    postalCode: true,
    city: true,
    serviceTypes: true,
    cuisines: true,
    dietaryOptions: true,
    dietaryCounts: true,
    needsStaff: true,
    needsTableware: true,
    needsDelivery: true,
    notes: true,
    brief: true,
    commissionRateBp: true,
}).extend({
    /** Die eingeladene Adresse — zum Vorbelegen der Registrierung, nicht als Bedingung. */
    email: z.email(),
    /** Der Name, den der Betreiber notiert hat. Vorbelegung, keine Stammdaten. */
    companyName: z.string(),
    /** Schon angenommen? Dann führt die Seite zur Anmeldung statt zur Registrierung. */
    claimed: z.boolean(),
});
/** Antwort auf das Annehmen. Danach öffnet das Portal die gewöhnliche Angebotsmaske. */
export const RfqExternalInviteClaimResultSchema = z.object({
    rfqId: z.string().min(1),
    /** `true`, wenn dieser Betrieb die Einladung schon vorher angenommen hatte. */
    alreadyClaimed: z.boolean(),
    /**
     * Ein gewöhnlicher Angebotslink (Scope `offer`, D7) für **diesen** Betrieb und **diese**
     * Ausschreibung — derselbe, den die Einladungsmail eines regulär eingeladenen Betriebs trägt.
     *
     * Seit D33 erkennt die Angebotsmaske den angemeldeten Betrieb auch über seine Sitzung. Der
     * Link bleibt trotzdem der Weg nach dem Annehmen: Er trägt auch dann noch, wenn die Sitzung
     * des frisch registrierten Caterers abläuft, während er sein Angebot schreibt.
     */
    offerToken: z.string().min(8),
});
/** Eine Zeile der Einladungsliste in der Admin-Prüfansicht. */
export const RfqExternalInviteAdminRowSchema = z.object({
    email: z.email(),
    companyName: z.string(),
    note: z.string(),
    status: RfqExternalInviteStatusSchema,
    commissionRateBp: z.int().min(0).max(10000),
    invitedAt: z.iso.datetime(),
    lastSentAt: z.iso.datetime(),
    sendCount: z.int().min(1),
    /** `null` heißt: Die letzte Mail ging nicht raus. */
    deliveredAt: z.iso.datetime().nullable(),
    claimedAt: z.iso.datetime().nullable(),
    claimedBySupplierId: z.string().nullable(),
    /** Aufgelöst über den Lieferantendatensatz — die Adresse allein sagt nicht, wer angenommen hat. */
    claimedByCompanyName: z.string().nullable(),
    /** Freischaltungsstand des annehmenden Betriebs. Vor der Übergabe zu prüfen (D24). */
    claimedBySupplierStatus: z.string().nullable(),
});
/* --------------------------------------------------------------------------------------------
 * Eingaben (Phase 5)
 * ----------------------------------------------------------------------------------------- */
/** Der Lieferant sagt ab. Der Grund ist optional, aber ausdrücklich erwünscht (§2.3). */
export const DeclineInviteInputSchema = z.object({
    reason: z.string().max(500).default(''),
});
/** Was der Admin beim Ausschreiben bestimmen darf. Alles optional — der Default trägt. */
export const RfqBroadcastInputSchema = z.object({
    /**
     * Abweichende Fensterlänge in Geschäftsstunden. Überspringt die Staffelung nach Vorlauf
     * (D12), **nicht** die Geschäftszeiten und nicht den Mindestabstand zur Veranstaltung.
     */
    bidWindowHours: z
        .int()
        .min(1)
        .max(24 * 14)
        .nullable()
        .default(null),
    /** Obergrenze der Einladungen. Deckelung nach oben, nie nach unten erzwungen (§C, Stufe 2). */
    maxInvites: z.int().min(1).max(25).nullable().default(null),
    /**
     * Ausdrückliche Auswahl statt Matching-Ergebnis. Für den Fall, dass der Betreiber die
     * Region kennt und den Automatismus übergeht — die IDs werden trotzdem gegen den
     * Hard-Filter geprüft, ein gesperrter Betrieb kommt so **nicht** herein.
     */
    supplierIds: z.array(z.string().min(1)).max(25).nullable().default(null),
    /**
     * Betriebe, die **trotz** eines Ausschlussgrundes eingeladen werden sollen.
     *
     * Der Fall, für den es das gibt: Der Matcher rechnet mit der eingetragenen Vorlaufzeit oder
     * der eingetragenen Höchstgästezahl — der Betreiber hat aber gerade telefoniert und weiß,
     * dass es diesmal passt. Ohne diesen Weg bliebe ihm nur, die Stammdaten des Betriebs zu
     * verbiegen, und die gälten dann für jede weitere Ausschreibung.
     *
     * **Zwei Gründe hebt auch das nicht auf**: ein nicht freigeschalteter Betrieb und eine
     * offene Provisionsrechnung. Beide sind Entscheidungen über den Betrieb selbst, nicht
     * Annahmen über diese eine Veranstaltung — der Server prüft sie unabhängig von dieser
     * Liste erneut.
     */
    forceIncludeSupplierIds: z.array(z.string().min(1)).max(25).nullable().default(null),
    /**
     * Betriebe, die noch kein Konto haben, per E-Mail einladen (D32).
     *
     * Der Fall: Die Matching-Vorschau ist leer, der Betreiber telefoniert Caterer in der Region
     * ab. Darum darf eine Ausschreibung mit null Treffern starten, **wenn** hier mindestens eine
     * Adresse steht — ohne das könnte der Caterer nach seiner Registrierung kein Angebot abgeben,
     * weil ohne Ausschreibung keine Frist läuft.
     */
    externalInvites: z.array(RfqExternalInviteInputSchema).max(10).default([]),
});
/** Eingriffe des Admins an einer laufenden Anfrage. */
export const RfqAdminActionSchema = z.discriminatedUnion('action', [
    z.object({
        action: z.literal('reject'),
        /** Pflicht: Eine Ablehnung ohne Begründung ist im Kundenkontakt nicht zustellbar. */
        reason: z.string().min(3).max(1000),
    }),
    z.object({ action: z.literal('cancel'), reason: z.string().max(1000).default('') }),
    /**
     * Die Übergabe freigeben (D24): Kontaktdaten fließen, beide Seiten bekommen ihre Mail.
     *
     * Ohne Parameter, weil es nichts zu entscheiden gibt — wann freigegeben wird, entscheidet
     * der Zahlungseingang auf der Provisionsrechnung, und den bucht der Betreiber ohnehin in
     * der Provisionsansicht. Der Endpunkt **prüft ihn nicht**: Es gibt legitime Fälle, in denen
     * vor Zahlung freigegeben wird (Stammkunde, Vorkasse per Telefon zugesagt, Rechnung im
     * Streit). Die Oberfläche warnt, der Server protokolliert; entscheiden darf der Mensch.
     */
    z.object({ action: z.literal('release_handover') }),
    z.object({
        action: z.literal('extend'),
        /** Zusätzliche Geschäftsstunden auf die bestehende Frist. */
        additionalHours: z
            .int()
            .min(1)
            .max(24 * 7),
    }),
]);
/* --------------------------------------------------------------------------------------------
 * Admin-Sichten (Phase 5)
 * ----------------------------------------------------------------------------------------- */
/** Zeile der Admin-Arbeitsliste. Bewusst schmal — die Liste lädt bis zu 50 Anfragen. */
export const RfqAdminListItemSchema = z.object({
    rfqId: z.string().min(1),
    status: RfqStatusSchema,
    title: z.string(),
    eventDate: z.iso.date(),
    eventStartTime: z.string(),
    guestCount: z.int(),
    postalCode: z.string(),
    city: z.string(),
    serviceTypes: z.array(ServiceTypeSchema),
    requesterCompany: z.string().nullable(),
    requesterEmail: z.email(),
    bidsCloseAt: z.iso.datetime().nullable(),
    inviteCount: z.int().min(0),
    offerCount: z.int().min(0),
    createdAt: z.iso.datetime(),
});
/**
 * Ein Treffer der Matching-Vorschau.
 *
 * Sie beantwortet dem Betreiber die Frage, die vor jedem Broadcast zählt: **Erreicht diese
 * Anfrage überhaupt jemanden?** Null Treffer sind kein Fehler, sondern ein Befund — und
 * besser vor dem Ausschreiben sichtbar als hinterher in einer Mail an den Kunden.
 */
export const RfqMatchCandidateSchema = z.object({
    supplierId: z.string().min(1),
    companyName: z.string(),
    city: z.string(),
    status: z.string(),
    travelSurchargeCents: z.int().min(0),
    commissionRateBp: z.int().min(0).max(10000),
    /** Warum dieser Betrieb **nicht** eingeladen würde. Leer heißt: er würde. */
    exclusionReasons: z.array(z.string()),
    /**
     * Ob der Betreiber diesen Betrieb **trotz** seiner Ausschlussgründe einladen darf.
     *
     * Nicht alle Gründe sind gleich. „Braucht 3 Tage Vorlauf" ist eine Annahme über den
     * Betrieb, die ein Anruf widerlegen kann; „Gesperrt wegen offener Provisionsrechnung" und
     * „Nicht freigeschaltet" sind Entscheidungen der Plattform, die eine Ausschreibung nicht
     * aushebeln darf. Die Unterscheidung fällt serverseitig
     * (`blockingExclusionReasons`) — dieses Feld transportiert nur ihr Ergebnis in die
     * Oberfläche, damit sie das Kästchen gar nicht erst anbietet.
     */
    overridable: z.boolean().default(true),
    /** Ob der Betreiber ihn tatsächlich von Hand hereingeholt hat (nur in der Antwort). */
    forcedInclude: z.boolean().default(false),
    /** Sortierwert der Deckelung (§C, Stufe 2). Höher ist besser. */
    score: z.number(),
});
/* --------------------------------------------------------------------------------------------
 * Zuschlag (Phase 6)
 * ----------------------------------------------------------------------------------------- */
/** Was der Kunde absendet, wenn er sich entschieden hat. */
export const RfqAwardInputSchema = z.object({
    supplierId: z.string().min(1),
});
/**
 * Die Kontaktdaten des **Kunden**, die der Gewinner mit dem Zuschlag bekommt — und niemand
 * sonst, nie früher.
 *
 * Eigenes Schema statt zusätzlicher Felder an `RfqSupplierViewSchema`, und das ist der
 * entscheidende Unterschied: Die Lieferantensicht kennt Straße, Kundenname und Telefonnummer
 * **strukturell nicht**. Sie können dort auch dann nicht auftauchen, wenn jemand in einem
 * Handler eine Bedingung falsch schreibt — er müsste dieses Objekt bewusst danebenstellen,
 * und genau das fällt in einem Review auf (§5, D17).
 */
export const AwardedRequesterContactSchema = z.object({
    contactName: z.string(),
    companyName: z.string().nullable(),
    email: z.email(),
    phone: z.string().nullable(),
    /** Jetzt vollständig: Straße, Hausnummer, Zugangshinweise. */
    street: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
    accessNotes: z.string().nullable(),
});
/** Die Kontaktdaten des **Lieferanten**, die der Kunde mit dem Zuschlag bekommt (D17). */
export const AwardedSupplierContactSchema = z.object({
    supplierId: z.string().min(1),
    companyName: z.string(),
    contactName: z.string(),
    email: z.email(),
    phone: z.string().nullable(),
    street: z.string().nullable(),
    postalCode: z.string(),
    city: z.string(),
});
